// Where the offline transport queues a write (BL-2431, ADR 066 — Offline Mode § 3 and § 4.6): the
// signed-in scope's change queue, registered by OfflineSyncProvider, so a queued write also reaches
// the Change queue tab and the not-synced counts. A write with no queue for its scope is refused.
import type { OfflineMediaStaging, OfflineScope, OutboxLocalWriteInput, OutboxLocalWriteResult } from '../../gateways/native-device';

/** The change queue as the offline transport sees it. */
export interface OfflineWriteQueue {
  /** The tenant and user whose queue this is. */
  scope: OfflineScope;
  /**
   * @param input - the write's entries and the lot records it changes.
   * @returns the stored entries, or `DUPLICATE_ID` with nothing written.
   * @sideEffect Writes to the device store and notifies the queue's readers.
   */
  queueLocalWrite(input: OutboxLocalWriteInput): Promise<OutboxLocalWriteResult>;
  /**
   * The scope's staged files (BL-2447): where a photo, video or document picked offline is kept
   * until the queue uploads it. Absent or null in a build without the Filesystem plugin.
   */
  media?: OfflineMediaStaging | null;
}

let active: OfflineWriteQueue | null = null;
/** Picks waiting for a queue to register (BUG-2081): each offline reload restarts the provider. */
const waiting = new Set<(queue: OfflineWriteQueue) => void>();

/**
 * Make a scope's change queue the one offline writes go to.
 *
 * @param queue - the scope and its queue.
 * @returns a function that unregisters it (and nothing a later registration put in its place).
 * @sideEffect Replaces the registered queue and hands it to every pick waiting for one.
 */
export function registerOfflineWriteQueue(queue: OfflineWriteQueue): () => void {
  active = queue;
  for (const resolve of [...waiting]) resolve(queue);
  return () => {
    if (active === queue) active = null;
  };
}

/**
 * @param scope - the scope the transport's store is open for.
 * @returns the registered queue when it belongs to that scope, else null (no write may be queued).
 */
export function offlineWriteQueueFor(scope: OfflineScope): OfflineWriteQueue | null {
  if (!active) return null;
  return active.scope.tenantId === scope.tenantId && active.scope.userId === scope.userId ? active : null;
}

/**
 * How long an offline write or pick waits for its scope's queue to register (BUG-2081, BUG-2147):
 * OfflineSyncProvider registers it once it has opened the device store, after each offline reload.
 */
export const OFFLINE_QUEUE_WAIT_MS = 10_000;

/**
 * The tenant's registered queue (and the user's, when named), or the next matching one to register
 * within `timeoutMs`. A file picked (BUG-2081) or a Save made (BUG-2147) while OfflineSyncProvider is
 * still opening the device store waits for it instead of failing. Another tenant's queue, or another
 * user's, never satisfies the wait, so a switch during it can't put the write in the wrong queue.
 *
 * @param input.tenantId - the tenant the file was picked or the write made in.
 * @param input.userId - the user the write is for; omitted for a pick, which knows no user.
 * @param input.timeoutMs - how long to wait when no matching queue is registered.
 * @returns the queue, or null when none registered in time.
 */
export function waitForOfflineWriteQueue(input: {
  tenantId: string;
  userId?: string;
  timeoutMs: number;
}): Promise<OfflineWriteQueue | null> {
  const matches = (queue: OfflineWriteQueue | null) =>
    queue?.scope.tenantId === input.tenantId && (input.userId === undefined || queue.scope.userId === input.userId);
  if (matches(active)) return Promise.resolve(active);
  return new Promise((resolve) => {
    const settle = (queue: OfflineWriteQueue | null) => {
      // Another tenant's (or user's) registration keeps the wait going.
      if (queue && !matches(queue)) return;
      // Called only after `timer` is set: by the timeout itself, or by a later registration.
      clearTimeout(timer);
      waiting.delete(settle);
      resolve(queue);
    };
    const timer = setTimeout(() => settle(null), input.timeoutMs);
    waiting.add(settle);
  });
}

/**
 * Where a file picked offline is staged (BL-2447): the signed-in scope's, the only one registered.
 *
 * @param scope - the scope to match, or omitted for the registered one (a picker knows no scope).
 * @returns the staged files, or null when there is no queue or no staging on this device.
 */
export function offlineMediaStaging(scope?: OfflineScope): OfflineMediaStaging | null {
  const queue = scope ? offlineWriteQueueFor(scope) : active;
  return queue?.media ?? null;
}
